import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, combineLatest, EMPTY, iif, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, finalize, first, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { EmploymentAgreementService } from 'src/app/data/employment-agreement.service';
import { WorkerService } from 'src/app/data/worker.service';
import { MatStepper } from '@angular/material/stepper';
import { EmploymentAgreementDto } from 'src/app/models/dtos/employment-agreement-dto';
import { EmploymentAgreementProcessStep } from 'src/app/common/enums/employment-agreement-process-step';
import { BasicInformationStepFormGroupService } from '../../services/basic-information-step-form-group.service';
import { InsuranceStepFormGroupService } from '../../services/insurance-step-form-group.service';
import { WageParametersStepFormGroupService } from '../../services/wage-parameters-step-form-group.service';
import { CheckEmploymentHistoryDialogService } from 'src/app/shared/messages/check-employment-history-dialog/service/check-employment-history-dialog.service';
import { CompanyDto } from 'src/app/models/dtos/CompanyDto';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { Messages, Permission } from 'src/app/common/enums';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { SubmitForLegalizationDto } from 'src/app/models/dtos/submit-for-legalization-dto';
import { LegalizationService } from 'src/app/data/legalization.service';
import { LegalizationDataForWorkerAgreementDto } from 'src/app/models/dtos/worker-agreements/legalization-data-for-worker-agreement-dto';
import { WorkerAgreementService } from 'src/app/data/worker-agreement.service';

@Component({
  selector: 'app-agreement-of-employment-form',
  templateUrl: './agreement-of-employment-form.component.html',
  styleUrls: ['./agreement-of-employment-form.component.scss'],
  standalone: false
})
export class AgreementOfEmploymentFormComponent implements OnInit, AfterViewInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private workerService = inject(WorkerService);
  private employmentAgreementService = inject(EmploymentAgreementService);
  private spinner = inject(NgxSpinnerService);
  private insuranceStepFormGroupService = inject(InsuranceStepFormGroupService);
  private basicInformationStepFormGroupService = inject(BasicInformationStepFormGroupService);
  private wageParametersStepFormGroupService = inject(WageParametersStepFormGroupService);
  private checkEmploymentHistoryDialogService = inject(CheckEmploymentHistoryDialogService);

  workerId: number;
  agreementId: number;
  isForeigner$ = new BehaviorSubject(false);
  workerAgreementLegalizationData: LegalizationDataForWorkerAgreementDto | null = null;
  protected employmentTypeId: number;
  private readonly authService = inject(AuthService);
  private readonly dictionaryService = inject(DictionaryService);
  private readonly dialog = inject(MatDialog);
  private readonly legalizationService = inject(LegalizationService);
  private readonly workerAgreementService = inject(WorkerAgreementService);
  private readonly router = inject(Router);

  private _prefetchedAgreement: EmploymentAgreementDto;

  set prefetchedAgreement(value: EmploymentAgreementDto) {
    const isChanged = this._prefetchedAgreement !== value;
    this._prefetchedAgreement = value;

    if (this.isPermittedToLegalization() && isChanged) {
      if (this.prefetchedAgreement.Id) {
        this.isForeigner$.next(this.prefetchedAgreement.IsForeigner);
        this.workerAgreementService
          .getLegalizationDataForWorkerAgreements([this.prefetchedAgreement.Id])
          .pipe(first())
          .subscribe(async (res) => {
            this.workerAgreementLegalizationData = res[0];
          });
      } else {
        this.workerService
          .getWorkerDataForAgreementForm([this.workerId])
          .pipe(first())
          .subscribe(res => this.isForeigner$.next(res[0].IsForeigner));
      }
    }
  }

  get prefetchedAgreement(): EmploymentAgreementDto {
    return this._prefetchedAgreement;
  }

  workerFullNameSubject = new BehaviorSubject<string>(null);

  insuranceStepFormGroup: UntypedFormGroup;
  basicInformationStepFormGroup: UntypedFormGroup;
  wageParametersStepFormGroup: UntypedFormGroup;
  private ignoreStepperSelectionChange = false;

  private unsubscribe$ = new Subject<void>();
  public listOfCompanies$: Observable<CompanyDto[]> = this.dictionaryService.getCompaniesList();
  public isLegalizationFieldVisible$: Observable<boolean>;

  @ViewChild('stepper', { static: false }) stepper: MatStepper;


  constructor() {
    const employmentTypeId = +this.route.snapshot.params.employmentTypeId;

    this.workerId = +this.route.snapshot.params.workerId;
    this.agreementId = +this.route.snapshot.params.agreementId;
    this.employmentTypeId = +this.route.snapshot.params.employmentTypeId;

    this.insuranceStepFormGroup = this.insuranceStepFormGroupService.buildInsuranceStepFormGroup();
    this.basicInformationStepFormGroup = this.basicInformationStepFormGroupService.buildBasicInformationStepFormGroup();
    this.wageParametersStepFormGroup = this.wageParametersStepFormGroupService.buildWageParametersStepFormGroup(this.employmentTypeId);
  }

  ngOnInit(): void {
    this.basicInformationStepFormGroup = this.basicInformationStepFormGroupService.buildBasicInformationStepFormGroup();
    this.fetchWorkerFullName();
    this.fetchAgreementById(this.agreementId);

    this.isLegalizationFieldVisible$ =
      combineLatest(
        [
          this.basicInformationStepFormGroup.get('company').valueChanges
            .pipe(startWith(this.basicInformationStepFormGroup.get('company').value))
            .pipe(distinctUntilChanged()),
          this.isForeigner$.pipe(distinctUntilChanged())
        ]
      ).pipe(switchMap(([companyId, isForeginer]) => iif(() => isForeginer,
        this.listOfCompanies$.pipe(map(companies => companies.find(company => company.Id === companyId)?.HasLegalization)),
        of(false)
      )));
  }

  ngAfterViewInit(): void {
    this.route.queryParams.subscribe((params) => {
      const step = +params['step'];
      if (!!step) {
        this.changeStep(step);
      } else {
        this.checkEmploymentHistoryDialogService.checkAndOpenModal(this.employmentTypeId);
      }
    });
    this.handleStepperStepChange();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  onBasicInformationSaved(): void {
    this.fetchAfterBasicInformationSaved();
  }

  onBasicInformationStepChange(agreement: EmploymentAgreementDto): void {
    this.prefetchedAgreement = agreement;
  }

  private fetchAgreementById(agreementId: number) {
    if (!agreementId){
      return;
    } 

    this.spinner.show();
    this.employmentAgreementService
      .getAgreementById(agreementId)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((agreement) => {
        this.prefetchedAgreement = agreement;
        this.basicInformationStepFormGroupService.patchFormGroupValue(this.basicInformationStepFormGroup, this.prefetchedAgreement);
      });
  }

  private fetchWorkerFullName() {
    this.workerService
      .getWorkerFullName(this.workerId)
      .pipe(first())
      .subscribe((fullname) => this.workerFullNameSubject.next(fullname));
  }

  private handleStepperStepChange() {
    this.stepper.selectionChange.pipe(takeUntil(this.unsubscribe$)).subscribe((event) => {
      if (this.ignoreStepperSelectionChange) {
        this.ignoreStepperSelectionChange = false;
        return;
      }

      const previouslySelectedIndex = event.previouslySelectedIndex + 1;

      if (previouslySelectedIndex === EmploymentAgreementProcessStep.BasicInformations && this.basicInformationStepFormGroup.invalid) {
        this.preventStepChange(EmploymentAgreementProcessStep.BasicInformations);
      }

      if (previouslySelectedIndex === EmploymentAgreementProcessStep.WageParameters && this.wageParametersStepFormGroup.invalid) {
        this.preventStepChange(EmploymentAgreementProcessStep.WageParameters);
      }
    });
  }

  fetchAfterBasicInformationSaved() {
    this.fetchAgreementById(this.agreementId);
  }

  private preventStepChange(stepIndex: number) {
    this.ignoreStepperSelectionChange = true;
    this.changeStep(stepIndex);
    this.stepper.selected.interacted = false;
  }

  private changeStep(stepIndex: number) {
    this.stepper.linear = false;
    this.stepper.selectedIndex = stepIndex - 1;
    this.stepper.linear = true;
  }

  private isPermittedToLegalization(): boolean {
    return this.authService.hasPermission(Permission.LegalizationInformationVisibilityToAll)
      || this.authService.hasPermission(Permission.LegalizationInformationVisibilityToEmployerObjects);
  }

  get isSubmitForLegalizationEnabled(): boolean {
    const formData = this.basicInformationStepFormGroup.getRawValue();
    return !!(formData.company && formData.employer && formData.employerObject);
  }

  onSubmitForLegalization(): void {
    if (!this.isSubmitForLegalizationEnabled) {
      this.markRequiredLegalizationFieldsAsInvalid();
      return;
    }

    this.dialog
      .open(ConfirmDialogComponent, { data: { title: Messages.ConfirmSubmitForLegalizationTitle, message: Messages.ConfirmSubmitForLegalization } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? this.submitForLegalization() : EMPTY)),
      )
      .subscribe();
  }

  private submitForLegalization(): Observable<void> {
    const data = this.basicInformationStepFormGroup.getRawValue();
    const payload: SubmitForLegalizationDto = {
      workerAgreementId: this.prefetchedAgreement?.Id,
      employmentTypeId: this.employmentTypeId,
      agreementTypeId: data.agreementType,
      workerId: this.workerId,
      companyId: data.company,
      employerId: data.employer,
      employerObjectId: data.employerObject?.Id,
      isImported: false,
      locationId: data.location?.Id,
      conclusionDate: data.conclusionDate,
      employmentDateFrom: data.employmentDateFrom,
      employmentDateTo: data.employmentDateTo,
      responsibilities: data.responsibilities?.map((res) => res.Id),
      potentialSupervisorId: data.supervisor?.Id,
      jobTitleId: data.jobTitle,
      organizationalUnitId: data.organizationalUnit?.Id,
      temporaryWorkplaceId: data.temporaryWorkplace?.Id,
      temporaryWorkplaceName: data.temporaryWorkplace ? (typeof data.temporaryWorkplace === 'object' ? data.temporaryWorkplace.Name : data.temporaryWorkplace) : null,
      recruiterId: data.recruiter
    };

    this.spinner.show();
    const action$ = this.legalizationService.submitForLegalization(payload);

    return action$.pipe(
      finalize(() => this.spinner.hide()),
      tap(() => {
        this.router.navigate(['/legalizations/successfullySubmitForLegalization', this.workerId]);
      }),
    );
  }

  private markRequiredLegalizationFieldsAsInvalid(): void {
    const requiredFields = ['company', 'employer', 'employerObject'];

    requiredFields.forEach(fieldName => {
      const control = this.basicInformationStepFormGroup.get(fieldName);
      if (!control.value) {
        control.setErrors({ required: true });
        control.markAsTouched();
      }
    });
  }
}
